import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * AI insight card — the signature "AI Executive at work" surface.
 * Two-column: Completed (green) + Needs attention (amber), with an optional health metric on the right.
 * Props:
 *   eyebrow: e.g. "AI Vendor Executive"
 *   status: e.g. "Working normally · Today's activity"
 *   completed: string[]
 *   attention: string[]
 *   metricLabel, metricValue: for the top-right stat (e.g. "Cash flow health" / "94%")
 */
export function NexaAIInsight({ eyebrow, status, completed = [], attention = [], metricLabel, metricValue, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="relative overflow-hidden rounded-[18px] p-5 border border-white/10"
      style={{
        background: `
          radial-gradient(circle at 18% 18%, rgba(45,200,170,.16), transparent 42%),
          radial-gradient(circle at 90% 15%, rgba(243,197,90,.10), transparent 35%),
          linear-gradient(
            180deg,
            #0F5C58 0%,
            #0C4E4B 55%,
            #093B39 100%
          )
        `,
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,.06), 0 18px 40px rgba(0,0,0,.22)",
      }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className="ai-dot" />
          <div>
            <div className="text-[8px] tracking-[0.14em] uppercase text-white/90 font-semibold">{eyebrow}</div>
            {status && <div className="text-[9.5px] text-[#F4C542] mt-0.5">{status}</div>}
          </div>
        </div>
        {metricValue !== undefined && (
          <div className="text-right">
            <div className="text-[7.5px] tracking-[0.14em] uppercase text-white/90 font-semibold">{metricLabel}</div>
            <div className="mt-0.5 text-[16px] font-bold text-[#F4C542]">{metricValue}</div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <motion.div
          className="rounded-xl p-2.5"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)",
            border: "1px solid rgba(255,255,255,0.36)",
          }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          whileHover={{
            y: -5,
            scale: 1.01,
            boxShadow: "0 12px 30px rgba(244, 197, 66, 0.38)",
          }}
        >
          <motion.div
            className="flex items-center gap-1.5 mb-2"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
          >
            <motion.div
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                delay: 0.35,
                type: "spring",
                stiffness: 260,
              }}
            >
              <CheckCircle2
                className="w-3 h-3 text-[#F4C542]"
                strokeWidth={2}
              />
            </motion.div>

            <span className="text-[8px] tracking-[0.14em] uppercase font-semibold text-[#F4C542]">
              Completed
            </span>
          </motion.div>

          <ul className="space-y-1">
            {completed.map((l, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  delay: 0.45 + i * 0.08,
                  duration: 0.35,
                }}
                whileHover={{ x: 4 }}
                className="text-[10px] text-white/90 flex items-start gap-1.5"
              >
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.5 + i * 0.08,
                    type: "spring",
                    stiffness: 300,
                  }}
                  className="mt-1 w-1 h-1 rounded-full bg-white/90"
                />

                <span>{l}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      
       <motion.div
        className="rounded-xl p-3"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)",
          border: "1px solid rgba(255,255,255,0.36)",
        }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        whileHover={{
          y: -5,
          scale: 1.01,
          boxShadow: "0 12px 30px rgba(244, 197, 66, 0.38)",
        }}
      >
        <motion.div
          className="flex items-center gap-1.5 mb-2"
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25, duration: 0.4 }}
        >
          <motion.div
            initial={{ scale: 0, rotate: 90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              delay: 0.35,
              type: "spring",
              stiffness: 260,
            }}
          >
            <AlertTriangle
              className="w-3 h-3 text-[#F4C542]"
              strokeWidth={2}
            />
          </motion.div>

          <span className="text-[8px] tracking-[0.14em] uppercase font-semibold text-[#F4C542]">
            Needs attention
          </span>
        </motion.div>

        <ul className="space-y-1">
          {attention.map((l, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.45 + i * 0.08,
                duration: 0.35,
              }}
              whileHover={{ x: 4 }}
              className="text-[10px] text-white/90 flex items-start gap-1.5"
            >
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: 0.5 + i * 0.08,
                  type: "spring",
                  stiffness: 300,
                }}
                className="mt-1 w-1 h-1 rounded-full bg-white/90"
              />

              <span>{l}</span>
            </motion.li>
          ))}
        </ul>
      </motion.div>
      </div>
    </motion.div>
  );
}
